/** Fusion toolbar — slim Filters/View toggle + collapsible panel (court-safe, layout, export). */
import { useEffect, useMemo, useState } from 'react';
import type cytoscape from 'cytoscape';
import {
  BookOpen, Camera, ChevronDown, FileSpreadsheet, Filter, Gavel, GraduationCap, List, Save, Scale, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useFusionStore } from '../../stores/useFusionStore';
import { runNamedLayout, fitTo, visibleEles, LAYOUT_PRESETS, type LayoutPreset } from './fusionLayout';
import { exportCanvasSnapshot, type SnapshotMode } from '../../case-canvas/snapshot';
import { exportCanvasCsv } from '../../case-canvas/canvasCsv';
import { saveFusionCase, clearFusionCase, hasFusionCase } from '../../case-canvas/fusionCaseStore';
import { clearFusionCanvas, confirmClearFusionCanvas } from '../../case-canvas/clearFusionCanvas';

const cy = () => (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
const today = () => new Date().toISOString().slice(0, 10);
const PANEL_KEY = 'fusion.filtersPanelOpen';

function readPanelOpen(): boolean {
  try {
    const v = localStorage.getItem(PANEL_KEY);
    if (v === null) return false;
    return v === '1' || v === 'true';
  } catch {
    return false;
  }
}

/** Export stamping: demo → SYNTHETIC, real → case-stamped.
 *  `exportDisabled` is true on a real case while the demo overlay is on. */
export function FusionToolbar({
  exportMode = 'demo', exportCaseId, exportDisabled = false,
  showDemoGuideButton = false, demoGuideOpen = false, onToggleDemoGuide,
  onOpenDemoLibrary,
  onCleared,
  compact = false,
  level,
  onLevelChange,
  levelBusy = false,
  actionsOn = false,
  onActionsChange,
  actionsCount = 0,
}: {
  exportMode?: SnapshotMode; exportCaseId?: string; exportDisabled?: boolean;
  /** Real case only: current graph altitude (high = macro view, low = detail view). */
  level?: 'low' | 'high';
  /** When provided, shows the "Macro view / Detail view" toggle. */
  onLevelChange?: (level: 'low' | 'high') => void;
  /** Disable the toggle while the other altitude is loading. */
  levelBusy?: boolean;
  /** Real case only: whether the Actions layer (seizure targets → notices) is shown. */
  actionsOn?: boolean;
  /** When provided, shows the "Actions" layer toggle. */
  onActionsChange?: (on: boolean) => void;
  /** Seizure targets in the latest case-worker run (0 disables the toggle). */
  actionsCount?: number;
  showDemoGuideButton?: boolean;
  demoGuideOpen?: boolean;
  onToggleDemoGuide?: () => void;
  onOpenDemoLibrary?: () => void;
  onCleared?: () => void;
  /** @deprecated Prefer full-width panel under the tab strip. Kept for call-site compat. */
  compact?: boolean;
} = {}) {
  const courtSafe = useFusionStore((s) => s.courtSafe);
  const toggleCourtSafe = useFusionStore((s) => s.toggleCourtSafe);
  const showDemo = useFusionStore((s) => s.showDemo);
  const setShowDemo = useFusionStore((s) => s.setShowDemo);
  const showLegend = useFusionStore((s) => s.showLegend);
  const setShowLegend = useFusionStore((s) => s.setShowLegend);
  const resetFilters = useFusionStore((s) => s.resetFilters);
  const [preset, setPreset] = useState<LayoutPreset>('organic');
  const [savedFlash, setSavedFlash] = useState(false);
  const [hasSaved, setHasSaved] = useState(() => hasFusionCase());
  const [panelOpen, setPanelOpen] = useState(() => readPanelOpen());

  useEffect(() => {
    try {
      localStorage.setItem(PANEL_KEY, panelOpen ? '1' : '0');
    } catch { /* ignore */ }
  }, [panelOpen]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (courtSafe) n += 1;
    if (exportMode === 'demo' && !showDemo) n += 1;
    if (preset !== 'organic') n += 1;
    if (showLegend) n += 1;
    if (demoGuideOpen) n += 1;
    return n;
  }, [courtSafe, exportMode, showDemo, preset, showLegend, demoGuideOpen]);

  const onSave = () => {
    const c = cy(); if (!c) return;
    if (saveFusionCase(c)) { setHasSaved(true); setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1800); }
  };
  const onResetDemo = () => { clearFusionCase(); window.location.reload(); };

  const onClearCanvas = () => {
    if (!confirmClearFusionCanvas()) return;
    clearFusionCanvas();
    setHasSaved(false);
    toast.success('Canvas cleared');
    onCleared?.();
  };

  const onFit = () => { const c = cy(); if (c) fitTo(c, visibleEles(c), 55); };
  const applyPreset = (p: LayoutPreset) => {
    setPreset(p);
    const c = cy(); if (!c) return;
    runNamedLayout(c, p, visibleEles(c));
    fitTo(c, visibleEles(c), 55);
  };
  const onExport = () => {
    if (exportDisabled) return;
    const c = cy(); if (c) exportCanvasSnapshot(c, { mode: exportMode, courtSafe, day: today(), caseId: exportCaseId });
  };
  const onExportCsv = () => {
    if (exportDisabled) return;
    const c = cy(); if (c) exportCanvasCsv(c, { mode: exportMode, courtSafe, day: today(), caseId: exportCaseId });
  };

  const btn = 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap';
  const btnSecondary = `${btn} bg-secondary hover:bg-secondary/80 text-foreground border-border`;
  const btnActive = `${btn} bg-primary text-primary-foreground border-primary`;

  const toggle = (
    <button
      type="button"
      onClick={() => setPanelOpen((o) => !o)}
      aria-expanded={panelOpen}
      aria-controls="fusion-filters-panel"
      data-testid="fusion-filters-toggle"
      data-tour="fusion-filters-toggle"
      className={panelOpen ? btnActive : btnSecondary}
      title={panelOpen ? 'Hide canvas filters and view controls' : 'Show canvas filters and view controls'}
    >
      <Filter className="w-3.5 h-3.5" aria-hidden />
      Filters / View
      {activeCount > 0 && (
        <span
          className={`ml-0.5 min-w-[1.15rem] h-[1.15rem] px-1 rounded-full text-[10px] font-bold leading-[1.15rem] text-center ${
            panelOpen ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary text-primary-foreground'
          }`}
          aria-label={`${activeCount} active filters`}
        >
          {activeCount}
        </span>
      )}
      <ChevronDown className={`w-3 h-3 transition-transform ${panelOpen ? 'rotate-180' : ''}`} aria-hidden />
    </button>
  );

  const panel = panelOpen && (
    <div
      id="fusion-filters-panel"
      data-testid="fusion-filters-panel"
      data-tour="fusion-toolbar"
      className="flex items-center gap-1.5 flex-wrap text-sm w-full"
    >
      <button
        type="button"
        onClick={toggleCourtSafe}
        data-tour="fusion-court-safe"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${
          courtSafe
            ? 'bg-emerald-600 text-white border-emerald-700'
            : 'bg-secondary hover:bg-secondary/80 text-foreground border-border'
        }`}
        title="Dim every scored (inferred) element — leave only deterministic, court-defensible evidence"
      >
        {courtSafe
          ? <><Scale className="w-3.5 h-3.5" aria-hidden /> Court-safe: ON</>
          : <><Scale className="w-3.5 h-3.5" aria-hidden /> Court-safe view</>}
      </button>

      {exportMode === 'demo' && (
        <button
          type="button"
          onClick={() => setShowDemo(!showDemo)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${
            showDemo
              ? 'bg-secondary hover:bg-secondary/80 text-foreground border-border'
              : 'bg-primary hover:bg-brand-gold-hover text-primary-foreground border-primary/80'
          }`}
          title="Show or hide the synthetic demo dataset — hide it to see only the data you've added"
        >
          {showDemo ? 'Demo data: ON' : 'My data only'}
        </button>
      )}

      <div className="flex items-center rounded-md border border-border overflow-hidden bg-secondary/30" data-testid="fusion-layout-actions">
        {LAYOUT_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => applyPreset(p.id)}
            data-tour={`fusion-layout-${p.id}`}
            className={`px-2.5 py-1.5 text-xs border-r border-border last:border-r-0 transition-colors whitespace-nowrap ${
              preset === p.id ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <button type="button" onClick={onFit} data-testid="fusion-fit" data-tour="fusion-fit" className={btnSecondary}>
        Fit
      </button>

      {exportMode === 'demo' && (
        <>
          <button
            type="button"
            onClick={onSave}
            data-testid="fusion-save"
            data-tour="fusion-save"
            title="Save this canvas as a Fusion case — persists across reloads and Build/Canvas tab switches"
            className={savedFlash ? btnActive : btnSecondary}
          >
            <Save className="w-3.5 h-3.5" /> {savedFlash ? 'Saved' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onResetDemo}
            data-testid="fusion-reset-demo"
            data-tour="fusion-reset-demo"
            title={hasSaved ? 'Discard the saved Fusion case and reload the pristine demo' : 'Reload the pristine demo'}
            className={btnSecondary}
          >
            Reset demo
          </button>
        </>
      )}

      <button
        type="button"
        onClick={onExport}
        disabled={exportDisabled}
        data-testid="fusion-export"
        data-tour="fusion-export"
        title={exportDisabled ? 'Export disabled while demo surfaces are overlaid' : 'Export a PNG snapshot'}
        className={`${btnSecondary} disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <Camera className="w-3.5 h-3.5" /> Export
      </button>

      <button
        type="button"
        onClick={onExportCsv}
        disabled={exportDisabled}
        data-testid="fusion-export-csv"
        data-tour="fusion-export-csv"
        title={exportDisabled ? 'Export disabled while demo surfaces are overlaid' : 'Export canvas elements as CSV'}
        className={`${btnSecondary} disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
      </button>

      <button type="button" onClick={resetFilters} data-testid="fusion-reset-filters" data-tour="fusion-reset-filters" className={btnSecondary}>
        Reset filters
      </button>

      <button
        type="button"
        onClick={() => setShowLegend(!showLegend)}
        data-testid="fusion-legend-toggle"
        data-tour="fusion-legend-toggle"
        aria-pressed={showLegend}
        className={showLegend ? btnActive : btnSecondary}
        title={showLegend ? 'Hide Graph Legend' : 'Show Graph Legend'}
      >
        <List className="w-3.5 h-3.5" /> {showLegend ? 'Hide Legend' : 'Legend'}
      </button>

      {onOpenDemoLibrary && (
        <button
          type="button"
          onClick={onOpenDemoLibrary}
          data-testid="fusion-demo-library"
          data-tour="fusion-demo-library"
          className={btnSecondary}
          title="Open Fusion demo scenarios"
        >
          <GraduationCap className="w-3.5 h-3.5 text-primary" /> Demos
        </button>
      )}

      {showDemoGuideButton && onToggleDemoGuide && (
        <button
          type="button"
          onClick={onToggleDemoGuide}
          data-testid="fusion-demo-guide"
          data-tour="fusion-demo-guide"
          aria-pressed={!!demoGuideOpen}
          className={demoGuideOpen ? btnActive : btnSecondary}
          title={demoGuideOpen ? 'Hide the Demo Guide' : 'Show the step-by-step Demo Guide'}
        >
          <BookOpen className="w-3.5 h-3.5" /> Demo Guide
        </button>
      )}

      <button
        type="button"
        onClick={onClearCanvas}
        data-testid="fusion-clear-canvas-menu"
        data-tour="fusion-clear-canvas-menu"
        className={`${btnSecondary} text-red-600 hover:bg-red-500/15 hover:border-red-500/40`}
        title="Clear canvas — remove all nodes and edges"
      >
        <Trash2 className="w-3.5 h-3.5" /> Clear
      </button>
    </div>
  );

  void compact; // call-site compat — toolbar is always a full-width slide-under strip

  const levelToggle = onLevelChange && (
    <div
      className="flex items-center rounded-md border border-border overflow-hidden bg-secondary/30"
      role="group"
      aria-label="Graph altitude"
      data-testid="fusion-level-toggle"
      data-tour="fusion-level-toggle"
    >
      {([
        { id: 'high', label: 'Macro view', title: 'Story view: phase lanes, bare relay wallets folded per group; named targets and identities stay visible' },
        { id: 'low', label: 'Detail view', title: 'Every wallet and edge in the case graph' },
      ] as const).map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => { if (level !== opt.id) onLevelChange(opt.id); }}
          disabled={levelBusy}
          aria-pressed={level === opt.id}
          data-testid={`fusion-level-${opt.id}`}
          title={opt.title}
          className={`px-2.5 py-1.5 text-xs border-r border-border last:border-r-0 transition-colors whitespace-nowrap disabled:opacity-60 ${
            level === opt.id ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const actionsToggle = onActionsChange && (
    <button
      type="button"
      onClick={() => onActionsChange(!actionsOn)}
      disabled={levelBusy || actionsCount === 0}
      aria-pressed={actionsOn}
      data-testid="fusion-actions-toggle"
      data-tour="fusion-actions-toggle"
      className={`${actionsOn ? btnActive : btnSecondary} disabled:opacity-50 disabled:cursor-not-allowed`}
      title={actionsCount === 0
        ? 'No seizure targets in the latest case-worker run'
        : actionsOn
          ? 'Hide the Actions layer'
          : 'Show seizure targets as notice nodes (notice state, evidence missing)'}
    >
      <Gavel className="w-3.5 h-3.5" aria-hidden /> Actions
      <span className="ml-0.5 text-[10px] font-semibold tabular-nums" aria-label={`${actionsCount} seizure targets`}>
        {actionsCount}
      </span>
    </button>
  );

  return (
    <div className="shrink-0 border-b border-border bg-card/50" data-fusion-toolbar-root>
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2">
        {toggle}
        <button type="button" onClick={onFit} data-testid="fusion-fit-quick" data-tour="fusion-fit" className={btnSecondary} title="Fit graph in view">
          Fit
        </button>
        {levelToggle}
        {actionsToggle}
        <span className="text-[11px] text-muted-foreground hidden md:inline">
          {panelOpen ? 'Canvas controls' : activeCount > 0 ? `${activeCount} active` : 'Layout, court-safe, export…'}
        </span>
      </div>
      {panelOpen && (
        <div className="px-3 sm:px-4 pb-2 border-t border-border/60 bg-card/30">
          <div className="pt-2">{panel}</div>
        </div>
      )}
    </div>
  );
}